import {
  Fragment,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import type { Cotizacion, Pieza, Vendedor } from "../domain/types";
import { areaM2, calcularCotizacion } from "../domain/pricing";
import { FORMATO_RESUMEN, moneda } from "../domain/model";
import { agruparGarantias, garantiasDelPdf } from "../domain/garantias";

// Réplica en vivo del PDF de la cotización (plantilla "Construir HTML del
// PDF" de n8n, formato consolidado): misma hoja carta, mismos bloques y
// tipografías, armada en el navegador con el cálculo local para que el
// vendedor vea el documento mientras edita. Es una vista aproximada: el
// documento que se entrega lo genera n8n con precios validados (botón «Ver
// PDF real», VistaPreviaPdf). Si la plantilla de n8n cambia, esto se ajusta
// a mano; referencia: PDF real del 22-oct-2026.

const ANCHO_HOJA = 816; // carta a 96 dpi

const fecha = () =>
  new Date().toLocaleDateString("es-EC", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
const dos = (n: number) =>
  n.toLocaleString("es-EC", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const pct = (f: number) =>
  `${(f * 100).toLocaleString("es-EC", { maximumFractionDigits: 2 })}%`;

function medidas(p: Pieza): { dimension: string; m2: string } {
  const sin = { dimension: "—", m2: "—" };
  if (["ml", "cm_lineal"].includes(p.unidad))
    return p.anchoCm > 0
      ? { dimension: `${dos(p.anchoCm / 100)} ml`, m2: "—" }
      : sin;
  if (p.unidad === "unidad")
    return p.anchoCm > 0 && p.altoCm > 0
      ? {
          dimension: `${dos(p.anchoCm / 100)} × ${dos(p.altoCm / 100)}`,
          m2: "—",
        }
      : sin;
  if (p.areaM2 != null) return { dimension: "—", m2: dos(p.areaM2) };
  return p.anchoCm > 0 && p.altoCm > 0
    ? {
        dimension: `${dos(p.anchoCm / 100)} × ${dos(p.altoCm / 100)}`,
        m2: dos(areaM2(p)),
      }
    : sin;
}

interface Fila {
  id: string;
  variante: string;
  familia: string;
  material: string;
  cantidad: number;
  dimension: string;
  m2: string;
  total: number;
  // Solo las piezas llevan al editor; logística y add-ons no.
  esPieza?: boolean;
}

export function HojaPdf({
  c,
  vendedor,
  onIrAPieza,
}: {
  c: Cotizacion;
  vendedor: Vendedor | null;
  // Clic en una fila de pieza: el editor lleva a esa pieza (paso 2).
  onIrAPieza?: (id: string) => void;
}) {
  const r = calcularCotizacion(c),
    porPieza = new Map(r.piezas.map((p) => [p.id, p])),
    unitario = c.mostrarValorUnitario !== false,
    firma = vendedor?.firma,
    condiciones = [
      ["Forma de pago", c.condiciones.forma_pago],
      ["Tiempo de entrega", c.condiciones.tiempo_entrega],
      [
        "Valor presentado",
        c.ivaIncluido ? "Con IVA incluido" : "Sin IVA incluido",
      ],
      ["Validez de la oferta", c.condiciones.validez_oferta],
    ].filter(([, v]) => v?.trim()),
    garantias = garantiasDelPdf(c),
    // Solo generales: el bloque de siempre, sin subtítulos ni «cubre».
    agrupadas = garantias.some((g) => g.grupo !== null),
    bruto = r.bruto,
    conDescuento = r.descuento > 0;
  return (
    <div className="hoja-pdf" style={{ width: ANCHO_HOJA }}>
      <header className="hp-encabezado">
        <img src="/assets/logo-diez-visual.svg" alt="Diez Visual" />
        <div>
          <h1>Cotización {c.numeroOficial || "BORRADOR"}</h1>
          <p>{fecha()}</p>
        </div>
      </header>
      <dl className="hp-datos">
        {[
          ["Cliente", c.cliente.nombre],
          ["Encargado", c.cliente.encargado],
          ["Proyecto", c.cliente.nombre_proyecto],
          ["Dirección de facturación", c.cliente.direccion_facturacion],
          ["Vendedor", vendedor?.nombre ?? ""],
        ].map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      {c.formatoPdf === FORMATO_RESUMEN ? (
        <p className="hp-aviso">
          El formato «Resumen por tienda y zona» arma su propia tabla por
          tienda. Revísalo con «Ver PDF real».
        </p>
      ) : (
        c.sitios.map((s, i) => {
          const calc = r.sitios[i],
            productos = s.productos.map((pr) => ({
              id: pr.id,
              nombre: pr.nombre.trim() || "(sin nombre)",
              foto: pr.imagenes[0] as string | undefined,
              filas: pr.piezas.map(
                (p): Fila => ({
                  id: p.id,
                  variante: p.variante || "Pieza sin seleccionar",
                  familia:
                    p.personalizado || p.esPersonalizada
                      ? "Personalizado"
                      : p.familia,
                  material: p.material,
                  cantidad: p.cantidad,
                  ...medidas(p),
                  total: porPieza.get(p.id)?.total ?? 0,
                  esPieza: true,
                }),
              ),
            })),
            servicios: Fila[] = [
              ...(calc.logistica > 0
                ? [
                    {
                      id: "logistica",
                      variante: "Logística e instalación",
                      familia: "Servicios",
                      material: "",
                      cantidad: 1,
                      dimension: "—",
                      m2: "—",
                      total: calc.logistica,
                    },
                  ]
                : []),
              ...s.addons
                .filter((a) => a.costo > 0)
                .map((a) => ({
                  id: a.id,
                  variante: a.nombre || "Servicio adicional",
                  familia: "Servicios",
                  material: "",
                  cantidad: 1,
                  dimension: "—",
                  m2: "—",
                  total: a.costo,
                })),
            ];
          if (servicios.length)
            productos.push({
              id: "servicios",
              nombre: "Logística y servicios",
              foto: undefined,
              filas: servicios,
            });
          return (
            <section className="hp-sitio" key={s.id}>
              <h2>{s.nombre.trim() || "Sitio"}</h2>
              {s.direccion.trim() || s.ciudad ? (
                <p className="hp-sub">
                  {[s.direccion.trim(), s.ciudad].filter(Boolean).join(" · ")}
                </p>
              ) : null}
              <table>
                <thead>
                  <tr>
                    <th>Foto</th>
                    <th>Detalle</th>
                    <th>Materiales</th>
                    <th className="n">Q</th>
                    <th className="n">Dimensión mts</th>
                    <th className="n">M2</th>
                    {unitario ? <th className="n">Valor unitario</th> : null}
                    <th className="n">Valor total</th>
                  </tr>
                </thead>
                <tbody>
                  {productos
                    .filter((pr) => pr.filas.length)
                    .map((pr) => (
                      <Fragment key={pr.id}>
                        {pr.filas.map((f, j) => (
                          <tr
                            key={f.id}
                            className={
                              [
                                j === 0 ? "hp-primera" : "",
                                onIrAPieza && f.esPieza ? "hp-ir" : "",
                              ]
                                .filter(Boolean)
                                .join(" ") || undefined
                            }
                            {...(onIrAPieza && f.esPieza
                              ? {
                                  tabIndex: 0,
                                  title: "Ir a esta pieza",
                                  "aria-label": `Ir a la pieza ${f.variante}`,
                                  onClick: () => onIrAPieza(f.id),
                                  onKeyDown: (e: KeyboardEvent) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      onIrAPieza(f.id);
                                    }
                                  },
                                }
                              : {})}
                          >
                            {j === 0 ? (
                              <>
                                <td
                                  rowSpan={pr.filas.length}
                                  className="hp-foto"
                                >
                                  {pr.foto ? (
                                    <img src={pr.foto} alt="" />
                                  ) : null}
                                </td>
                                <td
                                  rowSpan={pr.filas.length}
                                  className="hp-detalle"
                                >
                                  {pr.nombre}
                                </td>
                              </>
                            ) : null}
                            <td>
                              <strong>{f.variante}</strong>
                              {f.familia ? <small>{f.familia}</small> : null}
                              {f.material ? <span>+{f.material}</span> : null}
                            </td>
                            <td className="n">{f.cantidad}</td>
                            <td className="n">{f.dimension}</td>
                            <td className="n">{f.m2}</td>
                            {unitario ? (
                              <td className="n">
                                {moneda(f.cantidad ? f.total / f.cantidad : 0)}
                              </td>
                            ) : null}
                            <td className="n">{moneda(f.total)}</td>
                          </tr>
                        ))}
                      </Fragment>
                    ))}
                </tbody>
              </table>
              <p className="hp-total-sitio">
                Total del sitio: <strong>{moneda(calc.total)}</strong>
              </p>
            </section>
          );
        })
      )}
      {conDescuento || c.ivaIncluido ? (
        <dl className="hp-subtotales">
          {conDescuento ? (
            <>
              <div>
                <dt>Subtotal</dt>
                <dd>{moneda(bruto)}</dd>
              </div>
              <div>
                <dt>Descuento ({pct(c.descuento)})</dt>
                <dd>
                  <strong>-{moneda(r.descuento)}</strong>
                </dd>
              </div>
            </>
          ) : null}
          {c.ivaIncluido ? (
            <>
              <div className="hp-separa">
                <dt>Subtotal sin IVA</dt>
                <dd>
                  <strong>{moneda(r.subtotal)}</strong>
                </dd>
              </div>
              <div>
                <dt>IVA ({c.ivaPct}%)</dt>
                <dd>
                  <strong>{moneda(r.iva)}</strong>
                </dd>
              </div>
            </>
          ) : null}
        </dl>
      ) : null}
      <div className="hp-total">
        <span>
          {c.ivaIncluido ? "Total incluido IVA" : "Total sin incluir IVA"}
        </span>
        <strong>{moneda(r.total)}</strong>
      </div>
      <h3>Condiciones de la cotización</h3>
      <dl className="hp-tabla">
        {condiciones.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      {garantias.length ? (
        <>
          <h3>Garantías</h3>
          <p className="hp-garantia">
            Garantía contra defectos de fábrica. No cubre mal uso, desgaste
            natural, manipulación de terceros ni fuerza mayor.
          </p>
          {agruparGarantias(garantias).map(({ grupo, filas }) => (
            <div key={grupo ?? "general"}>
              {agrupadas ? (
                <h4 className="hp-garantia-grupo">{grupo ?? "General"}</h4>
              ) : null}
              <dl className="hp-tabla hp-garantias">
                {filas.map((g, i) => (
                  <div key={i}>
                    <dt>
                      {g.categoria}
                      {agrupadas ? <small>{g.cubre}</small> : null}
                    </dt>
                    <dd>
                      {g.anios != null
                        ? `${g.anios} ${g.anios === 1 ? "año" : "años"}`
                        : ""}
                      {g.notas ? ` · ${g.notas}` : ""}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </>
      ) : null}
      <div className="hp-firma">
        <strong>{firma?.nombre || vendedor?.nombre}</strong>
        {[firma?.titulo, firma?.cargo].filter(Boolean).length ? (
          <span>
            {[firma?.titulo, firma?.cargo].filter(Boolean).join(" · ")}
          </span>
        ) : null}
        {firma?.ciudad || firma?.direccion ? (
          <span>
            <b>{firma?.ciudad}:</b> {firma?.direccion}
          </span>
        ) : null}
        {[firma?.telefono, firma?.correo].filter(Boolean).length ? (
          <span>
            {[firma?.telefono, firma?.correo].filter(Boolean).join(" · ")}
          </span>
        ) : null}
      </div>
      <p className="hp-pie">
        Diez Visual — señalética y comunicación visual. Cotización generada el{" "}
        {fecha()}.
      </p>
    </div>
  );
}

// La hoja a escala del ancho disponible (el panel lateral o el diálogo).
export function HojaEscalada(props: {
  c: Cotizacion;
  vendedor: Vendedor | null;
  onIrAPieza?: (id: string) => void;
}) {
  const marco = useRef<HTMLDivElement>(null),
    hoja = useRef<HTMLDivElement>(null),
    [medida, setMedida] = useState({ escala: 1, alto: 0 });
  useLayoutEffect(() => {
    const m = marco.current,
      h = hoja.current;
    if (!m || !h) return;
    const medir = () => {
      const escala = Math.min(1, m.clientWidth / ANCHO_HOJA);
      setMedida({ escala, alto: h.offsetHeight * escala });
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(m);
    ro.observe(h);
    return () => ro.disconnect();
  }, []);
  return (
    <div
      className="hoja-marco"
      ref={marco}
      style={{ height: medida.alto || undefined }}
    >
      <div
        ref={hoja}
        style={{
          transform: `scale(${medida.escala})`,
          transformOrigin: "top left",
        }}
      >
        <HojaPdf {...props} />
      </div>
    </div>
  );
}
